import { useRef, useState } from 'react';
import { Dialog, Popover } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { PriceAmountWithDetails } from '@/api';
import Button from '@/design_system/Button';
import { InputMoney } from '@/design_system/InputNumber';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconEdit from '@/icons/Edit.svg';
import IconError from '@/icons/Error.svg';
import IconUndo from '@/icons/Undo.svg';
import IconWarning from '@/icons/Warning.svg';
import {
  type ArticleThresholdType,
  getArticleCurrency,
  useResetThreshold,
  useUpdateArticle,
} from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import { type Currency, formatCurrency } from '@/utils/number';

export const Threshold = ({ type }: { type: ArticleThresholdType }) => {
  const { t } = useLingui();

  const { workflow, view: requestView } = useRequestContext();
  const { article, view: articleView, errors } = useArticleContext();

  const [isPopoverOpened, setIsPopoverOpened] = useState(false);
  const popoverAnchorRef = useRef<HTMLDivElement>(null);

  const requireQuote =
    type === 'client' ? article.clientRequireQuote : article.organizationRequireQuote;
  const isEditable =
    type === 'client'
      ? articleView.prices.thresholds.client.editable
      : articleView.prices.thresholds.organization.editable;

  const { mutateAsync: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: article.requestId,
  });

  const price =
    type === 'client'
      ? article.snapshot.organizationPrice?.amountPerCurrency?.[0]
      : article.snapshot.workshopPrice?.amountPerCurrency?.[0];
  const currency = getArticleCurrency(article, type === 'client' ? 'organization' : 'workshop');

  const hasNoThresholdData =
    !requireQuote &&
    !price &&
    !currency &&
    (type === 'organization'
      ? article.manualOrganizationThresholdAmount === null &&
        article.defaultOrganizationThresholdAmount === null
      : article.manualClientThresholdAmount === null &&
        article.defaultClientThresholdAmount === null);

  if (hasNoThresholdData && !isEditable) {
    return null;
  }

  const canEditThreshold = !!currency && !isPopoverOpened && isEditable && !requireQuote;
  const chooseArticleServiceTraits = workflow?.nodes.find(
    (node) => 'step' in node && node.step === 'service-choice'
  )?.config?.tasks?.choose_article_service?.traits;
  const isStoreValidationActor = !!chooseArticleServiceTraits?.includes('store');
  const organizationRequireQuoteLabel = isStoreValidationActor
    ? t({
        id: 'article.header.threshold.edit.require-quote.label.store',
        message: 'Store validation required',
      })
    : t({
        id: 'article.header.threshold.edit.require-quote.label.aftersales',
        message: 'Aftersales validation required',
      });
  const clientRequireQuoteLabel = t({
    id: 'article.header.threshold.edit.require-quote.label.client',
    message: 'Client validation required',
  });

  const thresholdLabel =
    type === 'client'
      ? article.clientRequireQuote
        ? clientRequireQuoteLabel
        : article.manualClientThresholdAmount !== null
          ? formatCurrency(article.manualClientThresholdAmount, currency)
          : article.defaultClientThresholdAmount !== null
            ? formatCurrency(article.defaultClientThresholdAmount, currency)
            : '-'
      : article.organizationRequireQuote
        ? organizationRequireQuoteLabel
        : article.manualOrganizationThresholdAmount !== null
          ? formatCurrency(article.manualOrganizationThresholdAmount, currency)
          : article.defaultOrganizationThresholdAmount !== null
            ? formatCurrency(article.defaultOrganizationThresholdAmount, currency)
            : '-';

  const _isPriceExceedingThreshold = isPriceExceedingThreshold({ article, price, type });

  const showError = !!(type === 'organization'
    ? errors.thresholds?.organization
    : errors.thresholds?.client);

  const showVATInfo =
    type === 'client'
      ? requestView.organizationPrice.showAmountBeforeTaxes
      : requestView.workshopPrice.showAmountBeforeTaxes;

  const thresholdLabelText = showVATInfo
    ? t({ id: 'article.header.threshold.label', message: 'Threshold (incl. VAT)' })
    : t({ id: 'article.header.threshold.label.short', message: 'Threshold' });

  const thresholdAriaLabel = `${thresholdLabelText}${t({ id: '_general.colon', message: ':' })} ${type === 'client' ? requestView.organizationPrice.labelWithoutVATInfo : requestView.workshopPrice.labelWithoutVATInfo}`;

  const labelRow = (
    <Stack row gap="0.25rem" alignItems="center">
      {showError ? (
        <IconError className="text-danger" />
      ) : _isPriceExceedingThreshold ? (
        <IconWarning className="text-warning" />
      ) : null}
      <InlineText
        size="200"
        color={showError ? 'danger' : _isPriceExceedingThreshold ? 'warning' : 'secondary'}
      >
        {thresholdLabelText}
        <Trans id="_general.colon">:</Trans>
      </InlineText>
    </Stack>
  );

  const requireQuoteToggle = (
    <Toggle
      label={type === 'organization' ? organizationRequireQuoteLabel : clientRequireQuoteLabel}
      ariaLabel={
        type === 'organization'
          ? organizationRequireQuoteLabel
          : t({
              id: 'article.header.threshold.edit.require-quote.aria.client-price',
              message: 'Client validation required',
            })
      }
      alignment="left"
      size="small"
      isSelected={
        type === 'organization' ? article.organizationRequireQuote : article.clientRequireQuote
      }
      onChange={(nextRequireQuote) => {
        if (nextRequireQuote) {
          setIsPopoverOpened(false);
        }

        updateArticle({
          data:
            type === 'organization'
              ? { organizationRequireQuote: nextRequireQuote }
              : { clientRequireQuote: nextRequireQuote },
        });
      }}
    />
  );

  if (!isEditable) {
    return (
      <Stack
        row
        gap="0.25rem"
        alignItems="center"
        flexWrap="nowrap"
        style={{ borderRadius: '0.25rem' }}
        ariaLabel={thresholdAriaLabel}
      >
        {labelRow}
        <InlineText size="200" color={_isPriceExceedingThreshold ? 'warning' : undefined}>
          {thresholdLabel}
        </InlineText>
      </Stack>
    );
  }

  return (
    <Stack
      gap="0.25rem"
      alignItems="stretch"
      style={{ width: '100%', borderRadius: '0.25rem' }}
      ariaLabel={requireQuote ? undefined : thresholdAriaLabel}
    >
      {requireQuoteToggle}

      {!requireQuote && (
        <Stack
          row
          gap="0.5rem"
          alignItems="center"
          flexWrap="nowrap"
          ref={popoverAnchorRef}
          style={{ minWidth: 0 }}
        >
          {labelRow}
          <InlineText size="200" color={_isPriceExceedingThreshold ? 'warning' : undefined}>
            {thresholdLabel}
          </InlineText>

          {canEditThreshold && (
            <Button
              size="small"
              variant="secondary"
              iconOnly
              ariaLabel={t({
                id: 'article.header.threshold.edit.trigger',
                message: 'Edit threshold',
              })}
              tooltip={t({
                id: 'article.header.threshold.edit.tooltip',
                message: 'Edit',
              })}
              onPress={() => setIsPopoverOpened(true)}
            >
              <IconEdit />
            </Button>
          )}

          {!!currency && (
            <Popover
              placement="bottom left"
              triggerRef={popoverAnchorRef}
              isOpen={isPopoverOpened}
              onOpenChange={setIsPopoverOpened}
              offset={4}
            >
              <EditThresholdPopover thresholdType={type} currency={currency} />
            </Popover>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const { block } = createBEMClasses('edit-article-price-popover');

const EditThresholdPopover = ({
  thresholdType,
  currency,
}: {
  thresholdType: ArticleThresholdType;
  currency: Currency;
}) => {
  const { t } = useLingui();
  const { article } = useArticleContext();

  const {
    mutateAsync: updateArticle,
    isPending: isUpdatingArticle,
    isError,
  } = useUpdateArticle({
    articleId: article.id,
    requestId: article.requestId,
  });

  const commonInputMoneyProps = {
    ariaLabel: t({
      id: 'article.header.threshold.edit.input.label',
      message: 'Threshold amount',
    }),
    size: 'small' as 'small' | 'large',
    placeholder: '0,00',
    isLoading: isUpdatingArticle,
    currency,
    minValue: 0,
    error: isError ? t({ id: '_general.error.unknown', message: 'Unknown error' }) : undefined,
  };

  // Organization threshold popover
  if (thresholdType === 'organization') {
    return (
      <Dialog
        className={block()}
        aria-label={t({
          id: 'article.header.threshold.edit.organization',
          message: 'Edit workshop threshold',
        })}
      >
        <Stack gap="1rem">
          <Paragraph color="secondary">
            <Trans id="article.header.threshold.edit.content.organization">
              By default, the threshold is set to the internal cost. You can adjust it and will be
              notified if the quote exceeds this.
            </Trans>
          </Paragraph>

          <Stack row alignItems="center" gap="0.5rem">
            <InputMoney
              {...commonInputMoneyProps}
              value={
                article.manualOrganizationThresholdAmount ??
                article.defaultOrganizationThresholdAmount ??
                NaN
              }
              onChange={(threshold) => {
                updateArticle({
                  data: {
                    manualOrganizationThresholdAmount: isNaN(threshold) ? null : threshold,
                  },
                });
              }}
            />

            <ResetThresholdButton thresholdType={thresholdType} />
          </Stack>
        </Stack>
      </Dialog>
    );
  }

  // Client threshold popover
  return (
    <Dialog
      className={block()}
      aria-label={t({
        id: 'article.header.threshold.edit.client',
        message: 'Edit client threshold',
      })}
    >
      <Stack gap="1rem">
        <Paragraph color="secondary">
          <Trans id="article.header.threshold.edit.content.client">
            By default, the threshold is set to the client price. They can adjust it and will be
            contacted if the quote exceeds this.
          </Trans>
        </Paragraph>

        <Stack row alignItems="center" gap="0.5rem">
          <InputMoney
            {...commonInputMoneyProps}
            value={
              article.manualClientThresholdAmount ?? article.defaultClientThresholdAmount ?? NaN
            }
            onChange={(threshold) => {
              updateArticle({
                data: {
                  manualClientThresholdAmount: isNaN(threshold) ? null : threshold,
                },
              });
            }}
          />

          <ResetThresholdButton thresholdType={thresholdType} />
        </Stack>
      </Stack>
    </Dialog>
  );
};

const ResetThresholdButton = ({ thresholdType }: { thresholdType: ArticleThresholdType }) => {
  const { t } = useLingui();
  const { article } = useArticleContext();
  const { mutateAsync: resetThreshold, isPending: isResetThresholdPending } = useResetThreshold({
    articleId: article.id,
  });

  return (
    <Tooltip
      content={t({
        id: 'article.header.threshold.edit.input.reset-threshold.tooltip',
        message: 'Reset to default threshold',
      })}
    >
      <Button
        iconOnly
        variant="secondary"
        ariaLabel={t({
          id: 'article.header.threshold.edit.input.reset-threshold',
          message: 'Reset threshold',
        })}
        onPress={() => resetThreshold(thresholdType)}
        disabled={isResetThresholdPending}
      >
        <IconUndo />
      </Button>
    </Tooltip>
  );
};

const isPriceExceedingThreshold = ({
  article,
  price,
  type,
}: {
  article: ArticleWithRelations;
  price?: PriceAmountWithDetails;
  type: ArticleThresholdType;
}) => {
  if (type === 'organization') {
    return (
      !article.organizationRequireQuote &&
      !!price &&
      price.amount >
        (article.manualOrganizationThresholdAmount ??
          article.defaultOrganizationThresholdAmount ??
          0)
    );
  } else {
    return (
      !article.clientRequireQuote &&
      !!price &&
      price.amount >
        (article.manualClientThresholdAmount ?? article.defaultClientThresholdAmount ?? 0)
    );
  }
};
